import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailTower } from "../../../../../http/hrail/types/towers";
import { useHrailTowersApi } from "../../../../../http/hrail/useTowers";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateTowerModalDialogButton from "./CreateTowerModalDialogButton";
import TowerForm from "./TowerForm";

type Props = {
  active?: boolean;
};

const Towers: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "towers" });
  const aborter = useRef(new AbortController());
  const towerApi = useHrailTowersApi();
  const [itemToEdit, setItemToEdit] = useState<HrailTower>();
  const [itemToDelete, setItemToDelete] = useState<HrailTower>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "towers");
  const isActiveTab = active ?? true;
  const updateVisibility = async (tower: HrailTower, visibility: VisibilityScopeValue) => {
    try {
      await towerApi.updateAdminHrailTowerVisibility(tower.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (tower: HrailTower, newOrder: number) => {
    try {
      await towerApi.updateAdminHrailTowerOrder(tower.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.tower");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = towerApi.useAdminHrailTowers(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.towers.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.towers.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateTowerModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailTower>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="right">{i18n.translation.common.height}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(tower) => (
          <>
            <Table.Td noWrap={true}>{tower.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{tower.item?.description ?? ""}</Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(tower.heightMm, i18n.translation.common.millimeters)}
            </Table.Td>
            <td align="center">
              <Visibility value={tower.visibility} onChange={(value) => updateVisibility(tower, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={tower.order} onUpdate={(newOrder) => updateOrder(tower, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(tower) => setItemToEdit(tower)}
        rowActions={(tower) => [
          getEditAction(i18n, () => setItemToEdit(tower)),
          accessesAction(tower.item),
          getDeleteAction(i18n, () => setItemToDelete(tower), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.towers.edit}>
          <TowerForm tower={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.towers.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                towerApi
                  .deleteAdminHrailTower(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.towers.deleteLabel,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatNullableNumber = (value: number | null | undefined, suffix = "") =>
  typeof value === "number" ? String(value) + suffix : "";

export default Towers;
